import { WorkingHoursTimeOptionsHook } from '@models';
import { weekdayLabels } from '@services';
import { useStore } from '@stores';
import { MINUTES_IN_DAY, buildTimeOptions, timeToMinutes, toTimeOptions } from '@utils';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect } from 'react';

const STEP_MINUTES = 15;

/**
 * Time options in 15 minute steps, limited to the tenant's working hours for the given day.
 * Falls back to the full day when the garage has no work schedule for that weekday.
 */
export const useWorkingHoursTimeOptions = (date?: Dayjs): WorkingHoursTimeOptionsHook => {
  const { availabilityStore } = useStore();
  const { workAvailabilities, loading } = availabilityStore;

  useEffect(() => {
    // the work schedule is tenant wide, so it is fetched once and not cleared on unmount
    availabilityStore.getWorkAvailabilities();
  }, [availabilityStore]);

  // dayjs days start on Sunday, weekdayLabels start on Monday
  const weekday = weekdayLabels[((date ?? dayjs()).day() + 6) % 7];
  const slots = Array.from(workAvailabilities.values()).filter((slot) => slot.weekday === weekday);

  if (slots.length === 0) {
    return { options: buildTimeOptions(STEP_MINUTES), hasWorkingHours: false, loading };
  }

  const minutes: number[] = [];
  slots.forEach(({ startTime, endTime }) => {
    const start = timeToMinutes(startTime);
    const end = timeToMinutes(endTime);
    for (let minute = start; minute <= end && minute < MINUTES_IN_DAY; minute += STEP_MINUTES) {
      minutes.push(minute);
    }
    // keep the exact end of the shift when it does not fall on a step boundary
    if (end % STEP_MINUTES !== 0 && end < MINUTES_IN_DAY) {
      minutes.push(end);
    }
  });

  return { options: toTimeOptions(minutes), hasWorkingHours: true, loading };
};
